import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';

/**
 * Non-blocking toast that renders into document.body via a portal.
 * - success toasts auto-dismiss after `duration` ms (default 2500)
 * - error toasts stay until the user clicks ✕
 */
export const SaveToast = ({ toasts, onDismiss }) => {
  if (!toasts?.length) return null;
  return createPortal(
    <div
      aria-live="polite"
      className="fixed bottom-5 right-5 z-[9999] flex flex-col gap-2 pointer-events-none"
      style={{ maxWidth: 340 }}
    >
      {toasts.map(toast => (
        <div
          key={toast.id}
          className={`pointer-events-auto flex items-start gap-2.5 rounded-lg border px-4 py-3 shadow-lg text-sm transition-all duration-300
            ${toast.type === 'success'
              ? 'bg-white border-green-200 text-green-800'
              : 'bg-white border-red-200 text-red-700'
            }`}
        >
          <Icon
            name={toast.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
            size={16}
            className={`mt-0.5 flex-shrink-0 ${toast.type === 'success' ? 'text-green-600' : 'text-red-500'}`}
          />
          <span className="flex-1 leading-snug">{toast.message}</span>
          <button
            onClick={() => onDismiss(toast.id)}
            className="flex-shrink-0 text-muted-foreground hover:text-foreground transition-colors -mt-0.5"
            aria-label="Dismiss"
          >
            <Icon name="X" size={14} />
          </button>
        </div>
      ))}
    </div>,
    document.body
  );
};

/**
 * Hook that manages toast state, auto-dismiss timers, and cleanup.
 * Returns { toasts, addToast, dismissToast } to be spread into <SaveToast />.
 */
export const useSaveToast = (successDuration = 2500) => {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef({});

  const dismissToast = (id) => {
    clearTimeout(timersRef.current[id]);
    delete timersRef.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const addToast = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type }]);
    if (type === 'success') {
      timersRef.current[id] = setTimeout(() => dismissToast(id), successDuration);
    }
    // Error toasts persist until manually dismissed
  };

  // Clean up any pending timers on unmount
  useEffect(() => {
    return () => Object.values(timersRef.current).forEach(clearTimeout);
  }, []);

  return { toasts, addToast, dismissToast };
};

export default SaveToast;
